import { useEffect, useState } from 'react';

// Locally-persisted preferences. Values must be JSON-serializable.

export type PrefKey =
  | 'flagOverrides'
  | 'uploadLanguageCode'
  | 'exportTranscriptFormat'
  | 'exportTranscriptIncludeTimestamps'
  | 'exportTranscriptIncludeSpeakers'
  | 'exportTranscriptSpeakersInSeparateColumn'
  | 'captionLineLength'
  | 'reviewCorrectionHelpHidden'
  | 'youTubeImportHelpHidden'
  | 'referralBadgeClicked'
  | 'detailsPanelDirection'
  // Navigation preferences
  | 'nav.sidebarCollapsed'
  | 'nav.group.filesExpanded'
  | 'nav.group.reviewQueueExpanded'
  | 'nav.group.settingsExpanded'
  | 'nav.group.organizationSettingsExpanded'
  | 'nav.group.liveStreamsExpanded'
  // Video player preferences
  | 'player.captionOptions'
  | 'player.audioDescriptionOptions'
  | 'player.audioDescriptionMode'
  | 'player.highContrast'
  | 'player.playbackRate'
  | 'player.volume'
  | 'player.captionsEnabled'
  // Any table ID like 'table.files.sortColumn'
  | `table.${string}.sortColumn`
  | `table.${string}.sortAscending`
  | 'segmentExportFormat'
  | 'segmentExportIncludeTimestamps';

const prefix = 'recap.preference.';

export function deletePref(key: PrefKey): void {
  try {
    localStorage.removeItem(prefix + key);
  } catch (error) {}
}

export function setPref<T>(key: PrefKey, value: T): void {
  try {
    localStorage.setItem(prefix + key, JSON.stringify(value));
  } catch (error) {}
}

export function getPref<T>(key: PrefKey, defaultValue: T): T {
  try {
    const value = localStorage.getItem(prefix + key);
    return value !== null ? JSON.parse(value) : defaultValue;
  } catch (error) {
    return defaultValue;
  }
}

export function usePref<T>(
  key: PrefKey,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState(() => getPref(key, defaultValue));

  useEffect(() => {
    setPref(key, value);
  }, [key, value]);

  return [value, setValue];
}
